/**
 * Normalize various timestamp representations to a Date (or null if invalid).
 * Handles: Firestore Timestamp (with toDate), Date, plain { seconds, nanoseconds }, number (ms).
 * @param {Firestore.Timestamp|Date|{seconds: number, nanoseconds?: number}|number|null|undefined} ts
 * @returns {Date|null}
 */
function toDate(ts) {
  if (ts == null) return null;
  if (ts instanceof Date) return Number.isNaN(ts.getTime()) ? null : ts;
  if (typeof ts.toDate === 'function') {
    const d = ts.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
  if (typeof ts === 'number') return new Date(ts);
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a Firestore Timestamp or Date for display (date and time).
 * @param {Firestore.Timestamp|Date|null|undefined} ts
 * @returns {string|null} Formatted string or null if no valid timestamp
 */
export function formatTimestamp(ts) {
  const date = toDate(ts);
  if (!date) return null;
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
}

/**
 * Convert a Firestore Timestamp or Date to datetime-local input value (YYYY-MM-DDTHH:mm).
 * Uses LOCAL time (not UTC) so the input shows what the user expects.
 * @param {Firestore.Timestamp|Date|null|undefined} ts
 * @returns {string} Empty string if no valid timestamp
 */
export function toDateTimeLocal(ts) {
  const date = toDate(ts);
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
}

/**
 * Get a Date from a Firestore Timestamp or Date for comparisons.
 * @param {Firestore.Timestamp|Date|null|undefined} ts
 * @returns {Date|null} Date or null if invalid
 */
export function timestampToDate(ts) {
  return toDate(ts);
}
